import React from 'react';
import { compose } from 'underscore';
import {
  WithCampaignAudienceRepository,
  WithAdGroupAudienceRepository,
  WithAdGroupCriterionRepository,
  WithCampaignCriterionRepository,
} from '../repository';
import {
  convertToSummaryBarAudiences,
  convertToSummaryBarLocationsAndDevices,
} from './entity-converter';
import { CriteriaItems } from './criteria-items-content';

export class RepositoryConsumer {
  constructor({ scope, i18n }) {
    this.scope = scope;
    this.i18n = i18n;
  }

  get criteriaRenderer() {
    return {
      renderCamapignAudience: compose(this.consumeCampaignAudienceRepository, this.renderAudienceContent),
      renderAdGroupAudience: compose(this.consumeAdGroupAudienceRepository, this.renderAudienceContent),
      renderCampaignLocationDevice: isLocation => compose(this.consumeCampaignCriterionRepository, this.renderCampaignLocationDevicesContent(isLocation)),
      renderAdGroupLocationDevice: (isLocation, campaignSubType) => compose(this.consumeCampaignCriterionRepository, this.consumeAdGroupCriterionRepository, this.renderAgGroupLocationDeviceContent(isLocation, campaignSubType)),
    };
  }
  // audience
  renderAudienceContent = ({
    canEdit, ariaLabel, onClick,
  } = {}) => ({
    audienceFromOData,
  }) => {
    const audiences = audienceFromOData ? convertToSummaryBarAudiences(audienceFromOData)
      : null;

    return (<CriteriaItems
      targets={audiences}
      emptyValue={this.i18n.getString(_TL_('None'))}
      i18n={this.i18n}
      canEdit={canEdit}
      ariaLabel={ariaLabel}
      onClick={onClick}
    />);
  };

  consumeCampaignAudienceRepository = CampaignAudience => (
    (
      <WithCampaignAudienceRepository
        WrappedComponent={CampaignAudience}
        i18n={this.i18n}
      />
    )
  );

  consumeAdGroupAudienceRepository = AdGroupAudience => (
    (
      <WithAdGroupAudienceRepository
        WrappedComponent={AdGroupAudience}
        i18n={this.i18n}
      />
    )
  );

  // locations and devices
  renderLocationDeviceContent = (isLocation, locations, devices, {
    canEdit, ariaLabel, onClick, excludedLocationOnly,
  }) => (isLocation ?
    (<CriteriaItems
      targets={excludedLocationOnly ? [] : locations}
      emptyValue={this.i18n.getString(_TL_('All locations'))}
      i18n={this.i18n}
      canEdit={canEdit}
      ariaLabel={ariaLabel}
      onClick={onClick}
    />) : (<CriteriaItems
      targets={devices}
      emptyValue={this.i18n.getString(_TL_('None'))}
      i18n={this.i18n}
      canEdit={canEdit}
      ariaLabel={ariaLabel}
      onClick={onClick}
    />));

  renderCampaignLocationDevicesContent = isLocation => ({
    canEdit, ariaLabel, onClick, excludedLocationOnly,
  } = {}) => ({
    campaignCriterionFromOData,
  }) => {
    const campaignLocationsAndDevices = convertToSummaryBarLocationsAndDevices(campaignCriterionFromOData, this.i18n);
    const { locations, devices } = campaignLocationsAndDevices;

    return this.renderLocationDeviceContent(isLocation, locations, devices, {
      canEdit, ariaLabel, onClick, excludedLocationOnly,
    });
  };

  renderAgGroupLocationDeviceContent = (isLocation, campaignSubType) => ({
    canEdit, ariaLabel, onClick, excludedLocationOnly,
  } = {}) => ({
    adGroupCriterionFromOData,
    campaignCriterionFromOData,
  }) => {
    const campaignLocationsAndDevices = convertToSummaryBarLocationsAndDevices(campaignCriterionFromOData, this.i18n);
    const adGroupLocationsAndDevices = convertToSummaryBarLocationsAndDevices(adGroupCriterionFromOData, this.i18n, campaignLocationsAndDevices, campaignSubType);
    const { locations, devices } = adGroupLocationsAndDevices;

    return this.renderLocationDeviceContent(isLocation, locations, devices, {
      canEdit, ariaLabel, onClick, excludedLocationOnly,
    });
  };

  consumeCampaignCriterionRepository = CampaignCriterion => (
    (
      <WithCampaignCriterionRepository
        WrappedComponent={CampaignCriterion}
        i18n={this.i18n}
      />
    )
  );

  consumeAdGroupCriterionRepository = AdGroupCriterion => passThroughProps => (
    (
      <WithAdGroupCriterionRepository
        WrappedComponent={AdGroupCriterion}
        i18n={this.i18n}
        {...passThroughProps}
      />
    )
  );
}
